import { useState, useEffect } from "react";
import { X, Lock, Printer, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import ThermalReport from "./ThermalReport";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function FecharCaixaModal({ open, onClose, unidade, onClosed }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [fechamento, setFechamento] = useState(null);
  const [sessao, setSessao] = useState(null);

  useEffect(() => {
    if (open) {
      setPin("");
      setFechamento(null);
      api
        .get("/fechamentos/sessao-aberta", { params: { unidade_id: unidade.id } })
        .then((r) => setSessao(r.data))
        .catch(() => setSessao(null));
    }
  }, [open, unidade.id]);

  if (!open) return null;

  const confirmar = async () => {
    if (pin.length < 4) {
      toast.error("Informe o PIN da unidade");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/fechamentos", {
        unidade_id: unidade.id,
        senha: pin,
      });
      toast.success(`Fechamento #${res.data.numero} concluído`);
      setFechamento(res.data);
      if (onClosed) onClosed(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao fechar caixa");
    } finally {
      setLoading(false);
    }
  };

  const fechar = () => {
    setPin("");
    setFechamento(null);
    onClose();
  };

  if (fechamento) {
    const inicio = fechamento.periodo_inicio
      ? new Date(fechamento.periodo_inicio).toLocaleString("pt-BR")
      : "Início dos registros";
    const fim = new Date(fechamento.periodo_fim).toLocaleString("pt-BR");
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white border-4 border-neutral-900 w-full max-w-md max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900 print:hidden">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-600" strokeWidth={2.5} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Fechamento #{fechamento.numero}
                </p>
                <h3 className="font-display font-black text-lg tracking-tight">
                  Comprovante auditável
                </h3>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="fechamento-imprimir"
                onClick={() => window.print()}
                className="bg-yellow-400 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
              >
                <Printer className="w-4 h-4" /> Imprimir
              </button>
              <button
                data-testid="fechamento-fechar"
                onClick={fechar}
                className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4 bg-neutral-50">
            <ThermalReport
              titulo={`${unidade.nome} — Fechamento #${fechamento.numero}`}
              escopo={unidade.nome}
              periodo={`${inicio} → ${fim}`}
              vendas={fechamento.vendas_snapshot || []}
              mode="completo"
              hideProfitCost
            />
          </div>
        </div>
      </div>
    );
  }

  const totals = sessao || {
    qtd_vendas: 0,
    qtd_itens: 0,
    total_bruto: 0,
    total_desconto: 0,
    total_liquido: 0,
    lucro: 0,
  };
  const inicioStr = sessao?.periodo_inicio
    ? new Date(sessao.periodo_inicio).toLocaleString("pt-BR")
    : "Início dos registros";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white border-4 border-neutral-900 w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              {unidade.nome}
            </p>
            <h3 className="font-display font-black text-xl tracking-tight">
              Fechar Caixa Atual
            </h3>
          </div>
          <button onClick={fechar} className="p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-blue-50 border-2 border-blue-500 p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-900">
              Vai consolidar <strong>todas as vendas desde o último fechamento</strong>.
              Uma nova sessão abre automaticamente, e vendas futuras contam no próximo
              fechamento.
            </p>
          </div>

          <div className="border-2 border-neutral-900">
            <div className="bg-neutral-950 text-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest">
              Sessão atual · desde {inicioStr}
            </div>
            <div className="p-3 space-y-1 font-mono text-sm">
              <Row l="Vendas" v={totals.qtd_vendas} />
              <Row l="Itens vendidos" v={totals.qtd_itens} />
              <Row l="Faturamento Bruto" v={money(totals.total_bruto)} />
              <Row l="Descontos" v={`- ${money(totals.total_desconto)}`} />
              <div className="border-t border-dashed border-neutral-400 my-1" />
              <Row l="Faturamento Líquido" v={money(totals.total_liquido)} strong />
            </div>
          </div>

          {totals.qtd_vendas === 0 && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-300 p-2">
              Não há vendas nesta sessão. Registre ao menos uma venda para fechar o caixa.
            </div>
          )}

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Confirme com o PIN da unidade
            </span>
            <input
              data-testid="fechamento-pin-input"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              className="mt-1 w-full h-12 px-3 border-2 border-neutral-900 font-mono text-2xl tracking-[0.5em]"
              placeholder="••••"
              onKeyDown={(e) => e.key === "Enter" && confirmar()}
            />
          </label>
        </div>

        <div className="p-4 border-t-2 border-neutral-900 flex gap-2 justify-end">
          <button
            onClick={fechar}
            className="h-11 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
          >
            Cancelar
          </button>
          <button
            data-testid="fechamento-confirmar"
            onClick={confirmar}
            disabled={loading || pin.length < 4 || totals.qtd_vendas === 0}
            className="bg-neutral-950 text-white h-11 px-6 font-display font-bold uppercase text-xs tracking-widest disabled:opacity-40 disabled:cursor-not-allowed cta-brutal"
          >
            {loading ? "Processando..." : "Fechar Caixa"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, strong, className = "" }) {
  return (
    <div
      className={`flex justify-between ${strong ? "font-bold text-base" : ""} ${className}`}
    >
      <span>{l}</span>
      <span>{v}</span>
    </div>
  );
}
